import { motion } from "framer-motion";
import { ShieldCheck, Compass, Lightbulb, FileBadge } from "lucide-react";

const chapters = [
  {
    no: "01",
    id: "visi-misi",
    title: "Visi & Misi",
    body: "Membangun dan mengembangkan bisnis properti yang inovatif, terpercaya, dan memberikan kepuasan.",
    points: [
      "Memenuhi kebutuhan properti di berbagai wilayah Indonesia.",
      "Memberikan nilai investasi yang menguntungkan bagi pemegang saham.",
      "Memberikan manfaat bagi lingkungan dan pembangunan nasional.",
    ],
  },
  {
    no: "02",
    id: "keunggulan",
    title: "Alasan Memilih Kami",
    body: "Lebih dari sekadar penyedia gudang — kami adalah mitra pertumbuhan bisnis Anda.",
    cards: [
      { icon: ShieldCheck, title: "Pengalaman & Kepercayaan", desc: "Memiliki pengalaman dalam pengembangan properti dan kawasan pergudangan." },
      { icon: Lightbulb, title: "Konsep Modern & Inovatif", desc: "Menghadirkan bangunan dengan desain yang mengikuti kebutuhan bisnis masa kini." },
      { icon: Compass, title: "Lokasi Strategis", desc: "Dikembangkan di area yang mendukung akses distribusi dan pertumbuhan bisnis." },
    ],
  },
  {
    no: "03",
    id: "legalitas",
    title: "Legalitas & Jangkauan",
    body: "Berbadan hukum resmi dan terverifikasi — PT Planet Gudang Antar Galaksi berdiri sah di Kota Bekasi.",
    points: [
      "Pengesahan Kemenkumham RI, ditetapkan di Jakarta, 12 Maret 2026.",
      "Nomor Induk Berusaha (NIB) terbit melalui sistem OSS, 28 Maret 2026.",
      "Kegiatan usaha konstruksi gedung & perdagangan bahan bangunan terdaftar resmi.",
    ],
  },
];

export const Manifesto = () => {
  return (
    <section id="tentang" data-testid="manifesto-section" className="relative border-t border-[#28303f] bg-[#0f131a]">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-mono-tech mb-4 text-xs uppercase tracking-[0.35em] text-amber-400"
        >
          Tentang Perusahaan
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-display mb-6 max-w-3xl text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl lg:text-6xl"
        >
          Manifesto <span className="text-stroke-gold">Planet Gudang</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-20 max-w-2xl text-base leading-relaxed text-slate-400"
        >
          Pemenuhan kebutuhan properti, gudang, dan logistik memerlukan perencanaan yang matang serta lokasi yang tepat. Kami hadir sebagai solusi properti dan logistik yang mendukung kebutuhan bisnis Anda — menciptakan operasional yang lebih efektif dan bernilai jangka panjang.
        </motion.p>

        <div className="space-y-0">
          {chapters.map((ch, i) => (
            <motion.div
              key={ch.no}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`manifesto-chapter-${ch.id}`}
              className="relative grid grid-cols-1 gap-8 border-t border-[#28303f] py-16 lg:grid-cols-12"
            >
              <span className="font-display pointer-events-none absolute -top-6 right-0 select-none text-[8rem] font-black leading-none text-[#141923] lg:text-[12rem]">
                {ch.no}
              </span>
              <div className="relative lg:col-span-4">
                <span className="font-mono-tech text-xs uppercase tracking-[0.3em] text-amber-400">Bab {ch.no}</span>
                <h3 className="font-display mt-3 text-3xl font-bold uppercase tracking-tight text-slate-100 sm:text-4xl">{ch.title}</h3>
              </div>
              <div className="relative lg:col-span-8">
                <p className="max-w-xl text-base leading-relaxed text-slate-300">{ch.body}</p>

                {ch.points && (
                  <ul className="mt-8 space-y-4">
                    {ch.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-4 text-sm leading-relaxed text-slate-400" data-testid={`manifesto-${ch.id}-point-${j + 1}`}>
                        <span className="font-mono-tech mt-0.5 shrink-0 text-xs text-amber-400">{String(j + 1).padStart(2, "0")}</span>
                        {p}
                      </li>
                    ))}
                  </ul>
                )}

                {ch.cards && (
                  <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
                    {ch.cards.map((c) => (
                      <div
                        key={c.title}
                        data-testid={`keunggulan-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="group border border-[#28303f] bg-[#07090e] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/50"
                      >
                        <c.icon className="mb-4 h-6 w-6 text-amber-400 transition-transform duration-300 group-hover:scale-110" />
                        <h4 className="font-display text-lg font-bold uppercase tracking-wide text-slate-100">{c.title}</h4>
                        <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-4 flex items-center gap-4 border-t border-[#28303f] pt-10"
          data-testid="manifesto-badge"
        >
          <FileBadge className="h-5 w-5 text-amber-400" />
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-slate-500">
            PT Planet Gudang Antar Galaksi — Perseroan Swasta Nasional, Kota Bekasi
          </p>
        </motion.div>
      </div>
    </section>
  );
};
